import { customAlphabet } from 'nanoid';
import type { AnnotationStoreT as EntityTaggingStoreT, OutputAnnotationT as EntityTaggingOutputAnnotationT } from '../../entity-tagging/types';
import { ANNOTATION_TYPE } from '../../image/labelling/constants';
import { CSS_VARIABLE_HOVERED_STAGE_STROKE_WIDTH, CSS_VARIABLE_STAGE_STROKE_WIDTH } from '../../image/labelling/constants/stage-css-variables';
import type { AnnotationReviewStoreT, AnnotationStoreT as ImageLabellingStoreT, OutputAnnotationT as ImageLabellingOutputAnnotationT } from '../../image/labelling/types';
import type { OutputAnnotationT as TextLabelingOutputAnnotationT } from '../../text/labelling/types/studio-interface.type';
import type { AnnotationStoreT as TextLabelingAnnotationStoreT } from '../../text/labelling/types/types';
import { getColorPalette } from '../colors/color-palette';
import { SECONDS } from '../constant/constant';
import type useFeatureFlag from '../hooks/useFeatureFlag';
import type { FLAG_VALUES } from '../hooks/useFeatureFlag';
import type { ReviewActionT } from '../review-action/constants';
import { REVIEW_ACTION_TYPE } from '../review-action/constants';
import { ANNOTATION_FILTER_TYPE } from '../review-side-panel/constants/index';
import type { GetUpdatedAnnotationReviewParamsT } from '../review-side-panel/types/types';
import { getAnnotationStatus, getFilteredAnnotationIds } from '../review-side-panel/utils/utils';
import { getRandomRGBColor } from '../side-panel/segmentation-labels/utils';
import { groupAnnotationsWithLabel } from '../side-panel/utils/utils';
import { type TaskModeT, TASK_MODE } from '../task-state/constants';
import type { AnnotationLabelT, GenericAnnotationReviewDataT, GenericAnnotationT, QuestionsReviewDataT, TimeFormatT } from '../types/types';
import { type TabsT, TABS, UPDATE_ANNOTATION_TYPE } from '../types/types';
type AnnotationStoreT = ImageLabellingStoreT | EntityTaggingStoreT | TextLabelingAnnotationStoreT;
type OutputAnnotationT = ImageLabellingOutputAnnotationT | EntityTaggingOutputAnnotationT | TextLabelingOutputAnnotationT;
export const getRandomId = () => customAlphabet('0123456789abcdefghijklmnopqrstuvwxyz', 8)(); // 36^8 ≈ 2.8 trillion possibilities and collision percentage is 0% in 10K annotations, avoid special characters

export const getAnnotationsList = <T extends GenericAnnotationT,>(annotationStore: AnnotationStoreT): T[] => {
  if (!annotationStore) return [];
  return Object.keys(annotationStore).map(annotationKey => {
    const annotationData = annotationStore[annotationKey];
    // @ts-expect-error todo(ts-migration) TS2322 Type '{ id: string; } | { annotationType: "SEGMENTATION"; data: BrushDataT; metadata?: ({ answers?: AnswerT | undefined; mlData?: AnnotationMLDataT | undefined; originalId?: string | undefined; annota...
    const annotation: T = {
      id: annotationKey,
      ...annotationData
    };
    return annotation;
  });
};

/**
 * Given two stores with annotations having same original ID it will match the annotations
 * and return the list of ids matching this condition in the 2nd store.
 * @returns Object containing ids from store in 2nd argument
 */
export const getImportedMLAnnotationIds = (annotationStore: AnnotationStoreT, annotationMLStore?: AnnotationStoreT): {
  [x: string]: boolean;
} => {
  if (!annotationStore || !annotationMLStore) {
    return {};
  }
  const annotationsWithOriginalId: {
    [x: string]: boolean;
  } = {};
  Object.keys(annotationStore).forEach(annotationKey => {
    const annotation = annotationStore[annotationKey];
    // @ts-expect-error todo(ts-migration) TS18048 'annotation' is possibly 'undefined'.
    if (annotation.metadata?.originalId) {
      // @ts-expect-error todo(ts-migration) TS18048 'annotation' is possibly 'undefined'.
      annotationsWithOriginalId[annotation.metadata.originalId] = true;
    }
  });
  const importedMLAnnotationIds: {
    [x: string]: boolean;
  } = {};
  Object.keys(annotationMLStore).forEach(annotationKey => {
    const mlAnnotation = annotationMLStore[annotationKey];
    if (
    // @ts-expect-error todo(ts-migration) TS18048 'mlAnnotation' is possibly 'undefined'.
    mlAnnotation.metadata?.originalId &&
    // @ts-expect-error todo(ts-migration) TS18048 'mlAnnotation' is possibly 'undefined'.
    annotationsWithOriginalId[mlAnnotation.metadata.originalId]) {
      importedMLAnnotationIds[annotationKey] = true;
    }
  });
  return importedMLAnnotationIds;
};
export const getImportedConsensusAnnotationIds = (annotationStore: AnnotationStoreT, annotationConsensusStore?: AnnotationStoreT): {
  [x: string]: boolean;
} => {
  if (!annotationConsensusStore) {
    return {};
  }
  return Object.keys(annotationStore)
  // @ts-expect-error todo(ts-migration) TS2532 Object is possibly 'undefined'.
  .filter(annotationKey => annotationStore[annotationKey].metadata?.originalId).reduce((acc, annotationKey) => {
    if (annotationConsensusStore[annotationKey]?.metadata?.originalId) {
      acc[annotationKey] = true;
    }
    return acc;
  }, {});
};
export const getAnnotationsReviewDataList = (annotationReviewStore: AnnotationReviewStoreT): GenericAnnotationReviewDataT[] => {
  if (!annotationReviewStore) return [];
  // @ts-expect-error todo(ts-migration) TS2322 Type '(GenericAnnotationReviewDataT | undefined)[]' is not assignable to type 'GenericAnnotationReviewDataT[]'.
  return Object.keys(annotationReviewStore).map(annotationId => annotationReviewStore[annotationId]);
};
export const checkLabelMaxReached = (annotationLabel: AnnotationLabelT, annotationList: GenericAnnotationT[]) => {
  const addedAnnotationByLabel = groupAnnotationsWithLabel(annotationList);
  if (annotationLabel.max) {
    var annotationLabelCount = 0;
    addedAnnotationByLabel.forEach(groupedAnnotations => {
      if (groupedAnnotations.label.title === annotationLabel.title) {
        annotationLabelCount += groupedAnnotations.annotationList.length;
      }
    });
    if (annotationLabelCount >= annotationLabel.max) {
      return true;
    }
  }
  return false;
};
export const secondsToTime = (secs: number) => {
  const m = Math.floor(secs % 3600 / 60).toString().padStart(2, '0');
  const s = Math.floor(secs % 60).toString().padStart(2, '0');
  const ms = Math.floor(100 * (secs - Number(m) * 60 - Number(s))).toString().padStart(2, '0');
  return `${m}:${s}:${ms}`;
};
export const secondsToPreciseTime = (secs: number) => {
  const m = Math.floor(secs % 3600 / 60).toString().padStart(2, '0'),
    s = Math.floor(secs % 60).toString().padStart(2, '0'),
    ms = Math.floor(100 * (secs - Number(m) * 60 - Number(s))).toString().padStart(2, '0');
  return m + ':' + s + ':' + ms;
};
export const timeToSeconds = (time: string) => {
  const [m, s] = time.split(':');
  // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
  return parseInt(m) * 60 + parseInt(s);
};
export const getOutputReviewData = (annotationReviewStore: AnnotationReviewStoreT): GenericAnnotationReviewDataT[] => {
  // @ts-expect-error todo(ts-migration) TS2322 Type '(GenericAnnotationReviewDataT | undefined)[]' is not assignable to type 'GenericAnnotationReviewDataT[]'.
  return Object.keys(annotationReviewStore).map(key => {
    return annotationReviewStore[key];
  });
};
export const createAnnotationReviewData = (annotation: GenericAnnotationT): GenericAnnotationReviewDataT => {
  return {
    annotationId: annotation.id,
    status: REVIEW_ACTION_TYPE.PENDING,
    ...(annotation.annotationLabel.questionnaire && {
      questions: getQuestionsReviewData(annotation)
    })
  };
};
export const getAnnotationReviewData = (id: string, annotationReviewDataList?: GenericAnnotationReviewDataT[] | null): GenericAnnotationReviewDataT | undefined | null => {
  if (!annotationReviewDataList) return;
  return annotationReviewDataList.find(annotationReviewData => annotationReviewData.annotationId === id);
};
export const getQuestionsReviewData = (annotation: GenericAnnotationT): QuestionsReviewDataT => {
  const questions = {};
  annotation.annotationLabel.questionnaire?.forEach(questionnaire => {
    questions[questionnaire.questionnaireId] = {
      status: REVIEW_ACTION_TYPE.PENDING
    };
  });
  return questions;
};
const syncAnnotationWithLabel = (annotation: OutputAnnotationT, annotationLabelMap: {
  [x: string]: AnnotationLabelT | undefined | null;
}) => {
  const annotationLabel = annotation.annotationLabel.title;
  if (annotationLabelMap[annotationLabel]) {
    annotation.annotationLabel = annotationLabelMap[annotationLabel];
    // for brush add color to annotationData
    // colors for labels are generated in f7-ulabel and can be different for already created annotations
    if (annotation.annotationType === ANNOTATION_TYPE.SEGMENTATION) {
      annotation.data.color = annotationLabelMap[annotationLabel].style.strokeColor;
    }
  } else {
    // Instance segmentation creates new labels which is not present in label map. The below code appends the missing style field.
    const color = annotation.annotationType === 'SEGMENTATION' ? annotation.data.color : getRandomRGBColor();
    annotation.annotationLabel = ({
      ...annotation.annotationLabel,
      style: {
        strokeColor: color,
        fillColor: color,
        fillOpacity: 0.5,
        strokeWidth: 1
      }
    } as AnnotationLabelT);
  }
};
export const createAnnotationStore = (annotations: ImageLabellingOutputAnnotationT[] | EntityTaggingOutputAnnotationT[] | TextLabelingOutputAnnotationT[], annotationLabelMap: {
  [x: string]: AnnotationLabelT | undefined | null;
}) => {
  return (annotations || []).reduce((annotationStore, annotation) => {
    syncAnnotationWithLabel(annotation, annotationLabelMap);
    const {
      id,
      ...annotationData
    } = annotation;
    if (annotation.annotationType === 'REGION' && !Array.isArray(annotationData.data)) {
      // @ts-ignore because annotation is union typed
      annotationData.data = [annotationData.data];
    }
    annotationStore[id] = annotationData;
    return annotationStore;
  }, {});
};
export const createAnnotationMLStore = (annotations: ImageLabellingOutputAnnotationT[], annotationLabelMap: {
  [x: string]: AnnotationLabelT | undefined | null;
}) => {
  return (annotations || []).reduce((annotationStore, annotation) => {
    syncAnnotationWithLabel(annotation, annotationLabelMap);
    const {
      id: originalId,
      ...annotationData
    } = annotation;
    if (!annotationData.metadata) {
      annotationData.metadata = {};
    }
    annotationData.metadata.originalId = originalId;
    annotationStore[getRandomId()] = annotationData;
    return annotationStore;
  }, {});
};
export const getTabOverrides = () => {
  return {
    TabPanel: {
      style: () => ({
        padding: '0px',
        height: '100%',
        width: '100%',
        /**
         * adding position: relative here so that components will use co-ordinates
         * with respect to the TabPanel. Also, this will be helpful in future because
         * components won't overlap with Tab option. Because z-index of TabHighlight is 1.
         * So, every component will render below the Tab option. For example: sidePopOverPanel
         */
        position: 'relative',
        overflow: 'auto'
      })
    }
  };
};
export const isRejectedMode = (annotationReviewStore: AnnotationReviewStoreT): boolean => {
  return Object.keys(annotationReviewStore).reduce((rejectedMode, id) => {
    // @ts-expect-error todo(ts-migration) TS2345 Argument of type 'GenericAnnotationReviewDataT | undefined' is not assignable to parameter of type 'GenericAnnotationReviewDataT'.
    if (getAnnotationStatus(annotationReviewStore[id]) === REVIEW_ACTION_TYPE.REJECT) {
      rejectedMode = true;
    }
    return rejectedMode;
  }, false);
};
export const getSidePanelTabs = (hasRequiredTaskMode: (requiredTaskMode: TaskModeT) => boolean, readOnly: boolean): TabsT[] => {
  if ((hasRequiredTaskMode(TASK_MODE.EDIT) || !readOnly) && (hasRequiredTaskMode(TASK_MODE.REVIEW) || hasRequiredTaskMode(TASK_MODE.REJECTED))) {
    return [TABS.EDIT, TABS.PENDING_ITEMS, TABS.REVIEW];
  } else if (hasRequiredTaskMode(TASK_MODE.REVIEW)) {
    return [TABS.REVIEW];
  } else {
    return [TABS.EDIT, TABS.PENDING_ITEMS];
  }
};
export function hasPendingItems(annotationStore: AnnotationStoreT, annotationReviewStore: AnnotationReviewStoreT, isReviewMode: boolean, reviewModeVersion?: number) {
  const annotationList = getAnnotationsList(annotationStore);
  const annotationReviewDataList = getAnnotationsReviewDataList(annotationReviewStore);

  // checking if there are pending annotations for review in review mode.
  if (isReviewMode) {
    if (reviewModeVersion === 2) {
      return false;
    }
    const filteredAnnotationList = getFilteredAnnotationIds([ANNOTATION_FILTER_TYPE.PENDING], annotationReviewDataList);
    if (filteredAnnotationList.length > 0) {
      return true;
    }
  }
  const unfilledQuestionnaire = annotationList.filter(annotation => (annotation.annotationLabel.questionnaire?.length || 0) > 0 && !annotation.metadata?.answers);
  return unfilledQuestionnaire.length > 0;
}

/**
 *
 * @param {*} time in milli-seconds
 * @param {*} type type in which output is expected.
 * @returns it returns string value of time in the type requested.
 */
export const getFormattedTime = (time: number, type: TimeFormatT) => {
  let formattedTime = null;
  switch (type) {
    case SECONDS:
      // @ts-expect-error missing null check
      formattedTime = time / 1000;
      break;
    default:
      // @ts-expect-error missing null check
      formattedTime = time;
      break;
  }
  // @ts-expect-error missing null check
  return formattedTime.toFixed(2);
};
export const getUpdatedAnnotationReview = ({
  updatedAnnotation,
  annotationReview,
  updateAnnotationType,
  status
}: GetUpdatedAnnotationReviewParamsT): GenericAnnotationReviewDataT => {
  const reviewData: GenericAnnotationReviewDataT = {
    annotationId: annotationReview.annotationId,
    status,
    note: annotationReview.note
  };
  switch (updateAnnotationType) {
    case UPDATE_ANNOTATION_TYPE.BASE_ANNOTATION_UPDATE:
      if (updatedAnnotation.annotationLabel.questionnaire) {
        reviewData.questions = initializeQuestionReviewData(updatedAnnotation.annotationLabel, status);
      }
      break;
    case UPDATE_ANNOTATION_TYPE.ANNOTATION_DATA_UPDATE:
      reviewData.questions = annotationReview.questions;
  }
  return reviewData;
};
export const getUpdatedAnnotationReviewData = (annotationReviewData: GenericAnnotationReviewDataT, annotationReviewUpdates: {
  status?: ReviewActionT;
  questions?: {
    [id: string]: {
      status: ReviewActionT;
    };
  };
  note?: string;
}): GenericAnnotationReviewDataT => {
  const reviewData = {
    ...annotationReviewData
  };
  if (annotationReviewUpdates.status) {
    reviewData.status = annotationReviewUpdates.status;
  }
  if (annotationReviewUpdates.questions) {
    const questions = {};
    Object.keys(annotationReviewUpdates.questions).forEach(questionnaireId => {
      // @ts-expect-error missing null check
      questions[questionnaireId] = annotationReviewUpdates.questions[questionnaireId];
    });
    reviewData.questions = questions;
  }
  if (annotationReviewUpdates.note) {
    reviewData.note = annotationReviewUpdates.note;
  }
  return reviewData;
};

/**
 * @param {*} annotationLabel: label of the annotation, used to find questionnaire.
 * @param {*} status: REVIEW_ACTION_TYPE
 * @returns
 *
 * function used to initialize questionReviewData when annotation is added.
 */

export const initializeQuestionReviewData = (annotationLabel: AnnotationLabelT, status: ReviewActionT) => {
  return (annotationLabel.questionnaire || []).reduce((questionsReview, questionnaire) => {
    questionsReview[questionnaire.questionnaireId] = {
      status
    };
    return questionsReview;
  }, {});
};

/**
 * @param {*} annotation
 * @param {*} annotationId
 * @param {*} isReviewMode: used to check if annotation added by reviewer will be automatically in ACCEPTED state.
 * @returns
 *
 * function used to initialize annotationReviewData when annotation is added.
 */
export const initializeAnnotationReviewData = (annotation: GenericAnnotationT, status: ReviewActionT): GenericAnnotationReviewDataT => {
  const reviewData = {
    annotationId: annotation.id,
    status,
    ...(annotation.annotationLabel.questionnaire && {
      questions: initializeQuestionReviewData(annotation.annotationLabel, status)
    })
  };
  return reviewData;
};
export const isQuestionnaireUpdated = (globalAnnotationState: GenericAnnotationT, localAnnotationState: GenericAnnotationT): boolean => {
  // we are comparing object based upon reference to know if answers of questionnaire is updated and prev answers exists.
  if (globalAnnotationState.metadata?.answers && globalAnnotationState.metadata?.answers !== localAnnotationState.metadata?.answers) {
    return true;
  }
  return false;
};
export const getAnnotation = (id: string, annotationList?: GenericAnnotationT[] | null): GenericAnnotationT | undefined | null => {
  return (annotationList || []).find(annotation => annotation.id === id);
};
export const hasRejectedAnnotation = (annotationReviewStore: AnnotationReviewStoreT) => {
  const annotationReviewDataList = getAnnotationsReviewDataList(annotationReviewStore);
  const filteredAnnotationList = getFilteredAnnotationIds([ANNOTATION_FILTER_TYPE.REJECTED], annotationReviewDataList);
  return filteredAnnotationList.length > 0;
};
export function rgbToHex(r, g, b) {
  return '#' + (1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1);
}

/**
 * util function to get parent label from a give label.
 * A label can be a parent label or instance label.
 * Instance label will be in-form PARENT-LABEL_X where X can be any numeric value
 */
export const getParentAnnotationLabelTitle = (title: string, annotationLabelList: AnnotationLabelT[]) => {
  //if it is a parent label
  if (annotationLabelList.some(annotationLabel => annotationLabel.title === title)) {
    return title;
  }
  return title.slice(0, title.lastIndexOf('_'));
};

// Maps strokeColor (solid shade, either theme) → whether it belongs to a known palette hue.
const lightPalette = getColorPalette('Light');
const darkPalette = getColorPalette('Dark');
const lightShadeByStrokeColor = new Map(lightPalette.flatMap((lightHue, index): [string, string][] => [[lightHue.solid, lightHue.light], [darkPalette[index]!.solid, lightHue.light]]));

// Maps strokeColor → matching 100-series shade for stage-only pill colors.
const lightShadeForPillByStrokeColor = new Map<string, string>(lightPalette.flatMap((lightHue, index): [string, string][] => [[lightHue.solid, lightHue.light], [darkPalette[index]!.solid, darkPalette[index]!.light]]));
export const getLightShadeForStrokeColor = (strokeColor: string): string | undefined => lightShadeForPillByStrokeColor.get(strokeColor);
export const getAnnotationStyle = (originalStyle, options) => {
  const {
    isFocused = false,
    useStageStrokeWidth = false,
    overrideOpacity = false,
    isLocked = false,
    isM4 = false
  } = options;
  const outputStyle = {
    ...originalStyle
  };

  // polygon/cuboid pass SVG-attribute keys (stroke/fill) instead of strokeColor/fillColor
  const strokeColor = originalStyle.strokeColor ?? originalStyle.stroke;
  if (isM4) {
    const lightFillColor = lightShadeByStrokeColor.get(strokeColor);
    if (lightFillColor) {
      outputStyle.fillColor = strokeColor;
      if (originalStyle.fill !== undefined) {
        outputStyle.fill = strokeColor;
      }
    }
    outputStyle.fillOpacity = 0.25;
  }
  if (isLocked) {
    if (!overrideOpacity) {
      if (isM4) {
        outputStyle.fillOpacity = Math.max(outputStyle.fillOpacity - 0.2, 0.15);
      } else {
        outputStyle.fillOpacity = outputStyle.fillOpacity - 0.2;
      }
    }
  } else if (isFocused) {
    if (!overrideOpacity) {
      outputStyle.fillOpacity = isM4 ? 0.5 : outputStyle.fillOpacity + 0.25;
    }
    outputStyle.strokeWidth = outputStyle.strokeWidth + 1;
  }
  if (useStageStrokeWidth) {
    outputStyle.strokeWidth = `var(${CSS_VARIABLE_STAGE_STROKE_WIDTH})`;
    if (isFocused) {
      outputStyle.strokeWidth = `var(${CSS_VARIABLE_HOVERED_STAGE_STROKE_WIDTH})`;
    }
  }
  return outputStyle;
};
export const insertIf = <T,>(state: T, condition: boolean): T | {} => {
  return condition ? state : {};
};
export const hasAnnotationLabel = (annotationLabel: AnnotationLabelT, currentFrameAnnotationIds: Iterable<string>, annotationIdToLabelMap: Map<string, AnnotationLabelT>): boolean => {
  for (const id of currentFrameAnnotationIds) {
    if (annotationIdToLabelMap.get(id)?.title === annotationLabel.title) {
      return true;
    }
  }
  return false;
};
export const checkSourceValidity = async (mediaUrl: string, videoType?: string) => {
  try {
    const res = await fetch(mediaUrl || '', {
      method: 'HEAD',
      ...(videoType && {
        headers: {
          'Content-Type': videoType
        }
      })
    });
    return res.ok;
  } catch (error) {
    return false;
  }
};

/**
 * Utility function to check if a feature flag is enabled
 * @param featureFlag - The feature flag to check
 * @param featureFlagResponse - The response from the feature flag provider
 */
export function isFeatureEnabled<T extends FLAG_VALUES>(featureFlag: T, featureFlagResponse: ReturnType<typeof useFeatureFlag<T>>): boolean {
  if (featureFlagResponse.isLoading) {
    return false;
  }
  const featureValue = featureFlagResponse.featureFlags?.[featureFlag];
  if (!featureValue) {
    return false;
  }
  return 'enabled' in featureValue && featureValue.enabled;
}